import {
  catalogNameForChromePart,
  frameEntryForRef,
  getGlobalElements,
  getPages,
  getSurfaceCard,
  getViewFromCard,
  getWidgets,
  listChromeFrame,
  listChromeFrameRefs,
  listPageKeys,
  normalizeTheme,
  resolvePageView,
} from "../preview/document.js";

export function indexVariables(list) {
  const vars = {};
  for (const item of list || []) {
    if (item && item.name) vars[item.name] = item.value;
  }
  return vars;
}

export function interpolate(value, vars) {
  if (typeof value !== "string") return value;
  return value
    .replace(/@\{([^}]+)\}/g, (_, name) => {
      const found = vars[name];
      return found == null ? "" : String(found);
    })
    .replace(/#\{([^}]+)\}/g, (_, name) => {
      const found = vars[name];
      return found == null ? "" : String(found);
    });
}

function semanticThemeBucket(tokens, activeTheme) {
  const semantic = tokens?.semantic;
  if (!semantic || typeof semantic !== "object" || Array.isArray(semantic)) return {};
  if (semantic[activeTheme] && typeof semantic[activeTheme] === "object") {
    return semantic[activeTheme];
  }
  const pascal = activeTheme === "light" ? "Light" : "Dark";
  if (semantic[pascal] && typeof semantic[pascal] === "object") {
    return semantic[pascal];
  }
  return {};
}

export function buildTokenContext(tokens, theme = "dark") {
  if (!tokens) return {};
  const resolved = {};
  const activeTheme = normalizeTheme(theme);
  const semanticBucket = semanticThemeBucket(tokens, activeTheme);

  for (const [key, value] of Object.entries(tokens.variables ?? {})) {
    if (typeof value === "string") resolved[key] = value;
  }

  for (const [key, value] of Object.entries(tokens.palette ?? {})) {
    resolved[key] = value;
  }

  for (const [key, value] of Object.entries(semanticBucket)) {
    resolved[key] = value;
  }

  const lookup = (name, seen = new Set()) => {
    if (seen.has(name)) return resolved[name] ?? "";
    seen.add(name);
    let raw = resolved[name];
    if (raw == null) return "";
    if (typeof raw === "string" && raw.startsWith("@{") && raw.endsWith("}")) {
      const inner = raw.slice(2, -1);
      const value = lookup(inner, seen);
      resolved[name] = value;
      return value;
    }
    resolved[name] = raw;
    return raw;
  };

  for (const key of Object.keys(semanticBucket)) {
    lookup(key);
  }

  return resolved;
}

export function isColorValue(value) {
  if (typeof value !== "string") return false;
  if (value === "transparent") return true;
  return value.startsWith("oklch(")
    || value.startsWith("rgba(")
    || value.startsWith("rgb(")
    || value.startsWith("#");
}

export function parseColor(value) {
  if (typeof value !== "string") return value;
  if (value === "transparent") return value;
  if (value.startsWith("oklch(") || value.startsWith("rgba(") || value.startsWith("rgb(")) return value;
  if (!value.startsWith("#")) return value;
  const hex = value.slice(1);
  if (hex.length === 8) {
    const a = parseInt(hex.slice(0, 2), 16) / 255;
    const r = parseInt(hex.slice(2, 4), 16);
    const g = parseInt(hex.slice(4, 6), 16);
    const b = parseInt(hex.slice(6, 8), 16);
    return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(3))})`;
  }
  if (hex.length === 6) return `#${hex}`;
  return value;
}

export function resolveColor(value, vars) {
  if (value == null) return value;
  return parseColor(interpolate(String(value), vars));
}
